import { readAdminSession } from '../admin/authProvider';
import { readProjectSettings } from '../admin/adminSettings';

export type PaymentOverviewStatus = 'failed' | 'succeeded';

export type PaymentOverviewRecord = {
    paymentIntentId: string;
    userId: string;
    userName: string;
    userEmail: string;
    purchaseType: string;
    planName: string;
    billingCountryCode: string;
    billingCurrency: string;
    finalAmount: number;
    discountAmount: number;
    status: string;
    providerPaymentId: string;
    createdAtUtc: string;
    updatedAtUtc: string;
};

export type PaymentOverviewResult = {
    items: PaymentOverviewRecord[];
    totalCount: number;
    page: number;
    pageSize: number;
};

export type PaymentOverviewQuery = {
    startDate: string;
    endDate: string;
    countryCode?: string;
    status?: PaymentOverviewStatus;
    page: number;
    pageSize: number;
};

export type PaymentCountryOption = {
    countryCode: string;
    countryName: string;
    isAirwallexSupported: boolean;
};

export type PremiumFunnelSummary = {
    ctaClicks: number;
    ctaUsers: number;
    premiumPageViews: number;
    premiumPageUsers: number;
    planClicks: number;
    planUsers: number;
    checkoutStarts: number;
    checkoutUsers: number;
    ctaToPageRate: number;
    pageToPlanRate: number;
    planToCheckoutRate: number;
};

export type PremiumFunnelSource = {
    source: string;
    clicks: number;
    users: number;
    lastClickedAtUtc: string;
};

export type PremiumFunnelPlan = {
    planId: string;
    planName: string;
    durationDays: number | null;
    currency: string;
    amount: number | null;
    clicks: number;
    users: number;
    checkoutStarts: number;
    checkoutUsers: number;
};

export type PremiumFunnelDay = {
    day: string;
    ctaUsers: number;
    premiumPageUsers: number;
    planUsers: number;
    checkoutUsers: number;
};

export type PremiumFunnelRecentEvent = {
    eventId: string;
    userId: string;
    userName: string;
    email: string;
    eventName: string;
    source: string;
    route: string;
    planName: string;
    currency: string;
    amount: number | null;
    platform: string;
    paymentIntentId: string;
    providerName: string;
    correlationId: string;
    stage: string;
    outcome: string;
    errorCode: string;
    httpStatus: number | null;
    appVersion: string;
    occurredAtUtc: string;
};

export type PremiumFunnelResult = {
    fromUtc: string;
    toUtc: string;
    summary: PremiumFunnelSummary;
    ctaSources: PremiumFunnelSource[];
    plans: PremiumFunnelPlan[];
    daily: PremiumFunnelDay[];
    recentEvents: PremiumFunnelRecentEvent[];
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

function trimTrailingSlash(value: string) {
    return value.replace(/\/+$/, '');
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );
    const apiRoot = /\/api\/v[^/]+$/i.test(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    return { apiRoot, adminToken };
}

function errorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        const value = record.errorMessage ?? record.message ?? record.error ?? record.title ?? record.detail;
        if (typeof value === 'string' && value.trim()) return value;
    }
    return fallback;
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) return payload as T;
    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) throw new Error(errorMessage(envelope, 'Payment overview request failed'));
    return envelope.data === undefined ? (payload as T) : envelope.data;
}

async function request<T>(
    path: string,
    query?: Record<string, string>,
    controller = 'AdminSubscription',
    init?: RequestInit,
): Promise<T> {
    const { apiRoot, adminToken } = getAdminApiConfig();
    const suffix = path ? `/${path}` : '';
    const url = new URL(`${apiRoot}/${controller}${suffix}`);
    Object.entries(query ?? {}).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
    });

    const response = await fetch(url, {
        ...init,
        headers: {
            Accept: 'application/json',
            ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
            ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
            ...init?.headers,
        },
    });
    const text = await response.text();
    let payload: unknown = null;
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch {
            payload = text;
        }
    }
    if (!response.ok) throw new Error(errorMessage(payload, `Payment overview request failed (${response.status})`));
    return unwrap(payload as T | AdminApiEnvelope<T>);
}

export type AdminSubscriptionPlan = {
    planId: string;
    name: string;
    durationDays: number;
    isActive: boolean;
};

export type PaymentLinkRecord = {
    paymentLinkId: string;
    paymentIntentId: string;
    userId: string;
    userName: string;
    userEmail: string;
    planId: string;
    planName: string;
    currency: string;
    amount: number;
    providerPaymentLinkId: string;
    providerName: string;
    source: string;
    url: string;
    status: string;
    active: boolean;
    expiresAtUtc: string;
    createdAtUtc: string;
    emailStatus: string;
    emailSentAtUtc?: string | null;
    emailAttemptCount: number;
    lastEmailErrorCode?: string | null;
    successEmailStatus: string;
    successEmailSentAtUtc?: string | null;
    successEmailAttemptCount: number;
    lastSuccessEmailErrorCode?: string | null;
};

export type PaymentEvidenceRecord = {
    evidenceEventId: string;
    userId: string;
    userEmail: string;
    planId: string;
    paymentIntentId: string;
    paymentLinkId: string;
    eventName: string;
    source: string;
    outcome: string;
    errorCode: string;
    safeDetails: string;
    occurredAtUtc: string;
};

export type SubscriptionUserLookup = {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    isActivated: boolean;
};

export async function lookupSubscriptionUser(emailOrMobile: string): Promise<SubscriptionUserLookup> {
    return request<SubscriptionUserLookup>('users/lookup', { emailOrMobile });
}

export async function getSubscriptionPlans(): Promise<AdminSubscriptionPlan[]> {
    return request<AdminSubscriptionPlan[]>('plans');
}

export async function createPaymentLink(input: {
    userId: string;
    planId: string;
    countryCode?: string;
    expiresInHours: number;
}): Promise<PaymentLinkRecord> {
    return request<PaymentLinkRecord>('payment-links', undefined, 'AdminSubscription', {
        method: 'POST',
        body: JSON.stringify(input),
    });
}

export async function getPaymentLinks(userId?: string): Promise<PaymentLinkRecord[]> {
    return request<PaymentLinkRecord[]>('payment-links', userId ? { userId } : undefined);
}

export async function deactivatePaymentLink(paymentLinkId: string): Promise<PaymentLinkRecord> {
    return request<PaymentLinkRecord>(
        `payment-links/${encodeURIComponent(paymentLinkId)}/deactivate`,
        undefined,
        'AdminSubscription',
        { method: 'POST' },
    );
}

export async function getPaymentEvidence(userId?: string): Promise<PaymentEvidenceRecord[]> {
    return request<PaymentEvidenceRecord[]>('payment-evidence', {
        ...(userId ? { userId } : {}),
        limit: '200',
    });
}

function normalizePremiumFunnel(value: unknown): PremiumFunnelResult {
    const record = asRecord(value);
    const summary = asRecord(record.summary);
    const normalizeList = (input: unknown) => Array.isArray(input) ? input.map(asRecord) : [];
    return {
        fromUtc: asString(record.fromUtc),
        toUtc: asString(record.toUtc),
        summary: {
            ctaClicks: asNumber(summary.ctaClicks),
            ctaUsers: asNumber(summary.ctaUsers),
            premiumPageViews: asNumber(summary.premiumPageViews),
            premiumPageUsers: asNumber(summary.premiumPageUsers),
            planClicks: asNumber(summary.planClicks),
            planUsers: asNumber(summary.planUsers),
            checkoutStarts: asNumber(summary.checkoutStarts),
            checkoutUsers: asNumber(summary.checkoutUsers),
            ctaToPageRate: asNumber(summary.ctaToPageRate),
            pageToPlanRate: asNumber(summary.pageToPlanRate),
            planToCheckoutRate: asNumber(summary.planToCheckoutRate),
        },
        ctaSources: normalizeList(record.ctaSources).map((item) => ({
            source: asString(item.source),
            clicks: asNumber(item.clicks),
            users: asNumber(item.users),
            lastClickedAtUtc: asString(item.lastClickedAtUtc),
        })),
        plans: normalizeList(record.plans).map((item) => ({
            planId: asString(item.planId),
            planName: asString(item.planName),
            durationDays: item.durationDays === null || item.durationDays === undefined ? null : asNumber(item.durationDays),
            currency: asString(item.currency),
            amount: item.amount === null || item.amount === undefined ? null : asNumber(item.amount),
            clicks: asNumber(item.clicks),
            users: asNumber(item.users),
            checkoutStarts: asNumber(item.checkoutStarts),
            checkoutUsers: asNumber(item.checkoutUsers),
        })),
        daily: normalizeList(record.daily).map((item) => ({
            day: asString(item.day),
            ctaUsers: asNumber(item.ctaUsers),
            premiumPageUsers: asNumber(item.premiumPageUsers),
            planUsers: asNumber(item.planUsers),
            checkoutUsers: asNumber(item.checkoutUsers),
        })),
        recentEvents: normalizeList(record.recentEvents).map((item) => ({
            eventId: asString(item.eventId),
            userId: asString(item.userId),
            userName: asString(item.userName),
            email: asString(item.email),
            eventName: asString(item.eventName),
            source: asString(item.source),
            route: asString(item.route),
            planName: asString(item.planName),
            currency: asString(item.currency),
            amount: item.amount === null || item.amount === undefined ? null : asNumber(item.amount),
            platform: asString(item.platform),
            paymentIntentId: asString(item.paymentIntentId),
            providerName: asString(item.providerName),
            correlationId: asString(item.correlationId),
            stage: asString(item.stage),
            outcome: asString(item.outcome),
            errorCode: asString(item.errorCode),
            httpStatus: item.httpStatus === null || item.httpStatus === undefined ? null : asNumber(item.httpStatus),
            appVersion: asString(item.appVersion),
            occurredAtUtc: asString(item.occurredAtUtc),
        })),
    };
}

export async function getPremiumFunnel(fromUtc: string, toUtc: string): Promise<PremiumFunnelResult> {
    const payload = await request<unknown>('', { fromUtc, toUtc }, 'AdminPremiumFunnel');
    return normalizePremiumFunnel(payload);
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown) {
    return value === null || value === undefined ? '' : String(value);
}

function asNumber(value: unknown) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function normalizePayment(value: unknown): PaymentOverviewRecord {
    const record = asRecord(value);
    return {
        paymentIntentId: asString(record.paymentIntentId ?? record.id),
        userId: asString(record.userId),
        userName: asString(record.userName),
        userEmail: asString(record.userEmail),
        purchaseType: asString(record.purchaseType),
        planName: asString(record.planName),
        billingCountryCode: asString(record.billingCountryCode).toUpperCase(),
        billingCurrency: asString(record.billingCurrency).toUpperCase(),
        finalAmount: asNumber(record.finalAmount),
        discountAmount: asNumber(record.discountAmount),
        status: asString(record.status).toLowerCase(),
        providerPaymentId: asString(record.providerPaymentId),
        createdAtUtc: asString(record.createdAtUtc ?? record.createdAt),
        updatedAtUtc: asString(record.updatedAtUtc ?? record.updatedAt),
    };
}

export async function getPaymentOverview(query: PaymentOverviewQuery): Promise<PaymentOverviewResult> {
    const payload = await request<unknown>('payments', {
        startDate: query.startDate,
        endDate: query.endDate,
        countryCode: query.countryCode ?? '',
        status: query.status ?? '',
        page: String(query.page),
        pageSize: String(query.pageSize),
    });
    const record = asRecord(payload);
    const items = Array.isArray(record.items) ? record.items : Array.isArray(payload) ? payload : [];
    return {
        items: items.map(normalizePayment),
        totalCount: asNumber(record.totalCount ?? items.length),
        page: asNumber(record.page ?? query.page),
        pageSize: asNumber(record.pageSize ?? query.pageSize),
    };
}

export async function getPaymentCountries(): Promise<PaymentCountryOption[]> {
    const payload = await request<unknown>('country-currencies');
    const nestedItems = asRecord(payload).items;
    const items: unknown[] = Array.isArray(payload) ? payload : Array.isArray(nestedItems) ? nestedItems : [];
    return items
        .map((value): PaymentCountryOption => {
            const record = asRecord(value);
            return {
                countryCode: asString(record.countryCode).toUpperCase(),
                countryName: asString(record.countryName),
                isAirwallexSupported: record.isAirwallexSupported === true,
            };
        })
        .filter((item: PaymentCountryOption) => item.countryCode)
        .sort((left, right) => (left.countryName || left.countryCode).localeCompare(right.countryName || right.countryCode));
}
